import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { Dispatch, SetStateAction } from 'react';
import { useContext, useEffect, useState } from 'react';
import { KaimonoWebRoutes } from 'src/config/routes';
import type { LargeCategorySchema, MiddleCategorySchema } from 'src/models/category/schema';
import { CategoryContext } from 'src/stores/Category';
import { I18nContext } from 'src/stores/I18n';
import { mixpanel } from 'src/utils/mixpanel';
import styles from './index.module.scss';
type Props = {
  isSubNaviOpen: boolean;
  setIsSubNaviOpen: Dispatch<SetStateAction<boolean>>;
};
export const SubNaviForSp: React.FC<Props> = ({ isSubNaviOpen, setIsSubNaviOpen }) => {
  const localeResources = useContext(I18nContext);
  const [selectedLargeCategory, setSelectedLargeCategory] = useState<LargeCategorySchema | null>(null);
  const [selectedMiddleCategory, setSelectedMiddleCategory] = useState<MiddleCategorySchema | null>(null);
  const category = useContext(CategoryContext);
  const router = useRouter();

  const closeWithClickOutSideMethod = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isSubNaviOpen) {
      e.stopPropagation();
      setIsSubNaviOpen(false);
    }
  };
  const largeCategoryClickHandler = (largeCategory: LargeCategorySchema) => {
    mixpanel.onTopNavCategoryClicked('large', largeCategory.largeCategoryId);
    if (largeCategory.middleCategoryIds.length === 0) {
      router.push(KaimonoWebRoutes.largeCategories.show(largeCategory.slug));
      setIsSubNaviOpen(false);
    } else {
      setSelectedLargeCategory(largeCategory);
    }
  };
  const middleCategoryClickHandler = (middleCategory: MiddleCategorySchema) => {
    mixpanel.onTopNavCategoryClicked('middle', middleCategory.middleCategoryId);
    if (middleCategory.smallCategoryIds.length === 0) {
      router.push(KaimonoWebRoutes.middleCategories.show(middleCategory.slug));
      setIsSubNaviOpen(false);
    } else {
      setSelectedMiddleCategory(middleCategory);
    }
  };
  useEffect(() => {
    if (!isSubNaviOpen) {
      setSelectedLargeCategory(null);
      setSelectedMiddleCategory(null);
    }
  }, [isSubNaviOpen]);
  return (
    <>
      <div className={clsx(styles.overlay, isSubNaviOpen && styles.isOpen)} onClick={closeWithClickOutSideMethod} />
      {/* 大カテゴリリスト */}
      <div className={clsx(styles.container, isSubNaviOpen && styles.isOpen)}>
        <div className={styles.menuContainer}>
          <span />
          <button
            className={styles.closeButton}
            onClick={() => setIsSubNaviOpen(false)}
            aria-label={localeResources.commons.dismiss}
          />
        </div>
        <ul>
          {category.largeCategories.map((largeCategory) => (
            <li
              className={styles.listItem}
              key={largeCategory.largeCategoryId}
              onClick={() => largeCategoryClickHandler(largeCategory)}
            >
              {largeCategory.name}
            </li>
          ))}
        </ul>

        {/* 中カテゴリリスト */}
        <div className={clsx(styles.childContainer, selectedLargeCategory && isSubNaviOpen && styles.isOpen)}>
          <div className={styles.menuContainer}>
            <button onClick={() => setSelectedLargeCategory(null)} className={styles.backButton}>
              {localeResources.commons.goBack}
            </button>
            <button
              className={styles.closeButton}
              onClick={() => setIsSubNaviOpen(false)}
              aria-label={localeResources.commons.dismiss}
            />
          </div>
          <ul>
            {selectedLargeCategory && (
              <li key={selectedLargeCategory.largeCategoryId}>
                <Link href={KaimonoWebRoutes.largeCategories.show(selectedLargeCategory.slug)}>
                  <a
                    onClick={() => mixpanel.onTopNavCategoryClicked('large', selectedLargeCategory.largeCategoryId)}
                    className={styles.listItem}
                  >
                    {`${selectedLargeCategory.name} ${localeResources.commons.topPage}`}
                  </a>
                </Link>
              </li>
            )}
            {selectedLargeCategory &&
              category.middleCategories
                .filter((category) => selectedLargeCategory.middleCategoryIds.includes(category.middleCategoryId))
                .map((middleCategory) => (
                  <li
                    className={styles.listItem}
                    key={middleCategory.middleCategoryId}
                    onClick={() => middleCategoryClickHandler(middleCategory)}
                  >
                    {middleCategory.name}
                  </li>
                ))}
          </ul>
        </div>
        {/* 小カテゴリリスト */}
        <div className={clsx(styles.childContainer, selectedMiddleCategory && isSubNaviOpen && styles.isOpen)}>
          <div className={styles.menuContainer}>
            <button onClick={() => setSelectedMiddleCategory(null)} className={styles.backButton}>
              {localeResources.commons.goBack}
            </button>
            <button
              className={styles.closeButton}
              onClick={() => setIsSubNaviOpen(false)}
              aria-label={localeResources.commons.dismiss}
            />
          </div>
          <ul>
            {selectedMiddleCategory && (
              <li key={selectedMiddleCategory.middleCategoryId}>
                <Link href={KaimonoWebRoutes.middleCategories.show(selectedMiddleCategory.slug)}>
                  <a
                    onClick={() => mixpanel.onTopNavCategoryClicked('middle', selectedMiddleCategory.middleCategoryId)}
                    className={styles.listItem}
                  >
                    {`${selectedMiddleCategory.name} ${localeResources.commons.topPage}`}
                  </a>
                </Link>
              </li>
            )}
            {selectedMiddleCategory &&
              category.smallCategories
                .filter((category) => selectedMiddleCategory.smallCategoryIds.includes(category.smallCategoryId))
                .map((smallCategory) => (
                  <li key={smallCategory.smallCategoryId}>
                    <Link href={KaimonoWebRoutes.smallCategories.show(smallCategory.slug)}>
                      <a
                        onClick={() => mixpanel.onTopNavCategoryClicked('small', smallCategory.smallCategoryId)}
                        className={styles.listItem}
                      >
                        {smallCategory.name}
                      </a>
                    </Link>
                  </li>
                ))}
          </ul>
        </div>
      </div>
    </>
  );
};
